import { qs, on, delegate, lockScroll, trapFocus } from '../lib/dom.js';

export function initNav() {
  const header = qs('.ap-header');
  const drawer = qs('#ap-nav-drawer');
  if (!header) return;

  // The current-page top-nav item's `is-active` + `aria-current="page"` are
  // rendered authoritatively on the server (inc/walker-menu.php, via the
  // Polylang-safe, URL-path-based atpl_nav_item_is_active()). We deliberately
  // do NOT re-derive active state on the client: an earlier title-slug lookup
  // (body.data-page → data-nav) disagreed with the server on translated menu
  // titles (e.g. "Contacte"/"Schimb"/RU) and, because classList.toggle() with a
  // boolean force-removes on every non-match, it wiped the server's is-active
  // and set nothing - the item lost its active styling shortly after load.
  // Leaving the server markup untouched keeps the active state stable.

  // Header scroll behaviour: hide on scroll-down (after a small threshold),
  // show on scroll-up. Always visible at the very top of the page. The
  // current direction is also written to <html data-scroll-dir="up|down">
  // so other components (e.g. listing-page sticky CTA) can react via CSS
  // and JS without each binding their own scroll listener.
  let scrolled = false;
  let lastY = window.scrollY;
  const HIDE_THRESHOLD = 80;
  const DIRECTION_NOISE = 4;
  document.documentElement.dataset.scrollDir = 'up';

  const onScroll = () => {
    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;

    if (delta > DIRECTION_NOISE && y > HIDE_THRESHOLD) {
      header.classList.add('is-hidden');
      document.documentElement.dataset.scrollDir = 'down';
      lastY = y;
    } else if (delta < -DIRECTION_NOISE) {
      header.classList.remove('is-hidden');
      document.documentElement.dataset.scrollDir = 'up';
      lastY = y;
    }

    // Always reveal at the very top
    if (y <= 8) header.classList.remove('is-hidden');

    // Existing scroll shadow
    const isScrolled = y > 8;
    if (isScrolled !== scrolled) {
      scrolled = isScrolled;
      header.classList.toggle('is-scrolled', scrolled);
    }
  };
  on(window, 'scroll', onScroll, { passive: true });
  onScroll();

  // Drawer open/close
  if (!drawer) return;
  let releaseFocus = null;

  const open = () => {
    if (typeof drawer.showModal === 'function') drawer.showModal();
    else drawer.setAttribute('open', '');
    lockScroll(true);
    // Skip auto-focusing the first element (the logo) - drawer is mobile-first
    // and the focus ring on the logo distracts from the menu list. Tab still
    // cycles correctly inside the drawer for keyboard users.
    releaseFocus = trapFocus(drawer, { skipInitialFocus: true });
  };
  const close = () => {
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
    lockScroll(false);
    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
  };

  delegate(document, 'click', '[data-action="open-drawer"]', (e) => { e.preventDefault(); open(); });
  delegate(drawer, 'click', '[data-action="close-drawer"]', (e) => { e.preventDefault(); close(); });
  // Click on backdrop closes
  on(drawer, 'click', (e) => { if (e.target === drawer) close(); });
  // ESC closes (native dialog handles this, but also for non-supporting browsers)
  on(drawer, 'cancel', (e) => { e.preventDefault(); close(); });
  on(drawer, 'close', () => { lockScroll(false); if (releaseFocus) { releaseFocus(); releaseFocus = null; } });
}
